import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, Typography } from '@mui/material';
import { useMessage } from '../../hooks/useMessage';
import { FieldDef, FormsProps, FormsResultsDTO } from '../../types/forms';
import { deleteBulkFormResponse, getFormsRows } from '../../api/forms.api';
import Loading from '../loading/Loading';
import FormsResultsTable from '../FormsResultsTable/FormsResultsTable';
import { transformFormResponses } from '../../utils/forms.utils';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import DataUpdateForm from './DataUpdateForm';
import { useEvents } from '../../hooks/useEvents';
import { useError } from '../../context/error-context';

const ViewAndEditUserTable = ({ formId, formAllFields, linkedFormId, formType, skin, formName }: FormsProps) => {
    const { showMessage, MessageContainer } = useMessage();
    const [ results, setResults ] = useState<FormsResultsDTO['responses']>([]);
    const [ resultsLoading, setResultsLoading ] = useState(true);
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ removeLoading, setRemoveLoading ] = useState(false);
    const [ editModalOpen, setEditModalOpen ] = useState(false);
    const [ editingResponseId, setEditingResponseId ] = useState('');
    const responseIdsToRemove = useRef<(string | number)[]>([]);
    const { subscribe } = useEvents();
    const { showError } = useError();

    let realFormId = +formId;

    if (linkedFormId) {
        realFormId = linkedFormId;
    }

    const fetchRows = () => {
        setResultsLoading(true);

        getFormsRows(realFormId, formId)
        .then(response => {
            setResults(response.responses);
        })
        .catch((err) => {
            showError({
                code: err.response?.status || 500,
                message: 'API Error',
                description: 'Failed to fetch data',
                showRefresh: true,
            });
        })
        .finally(() => setResultsLoading(false));
    };

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ realFormId, formId ]);

    useEffect(() => {
        return subscribe(`REFRESH_FORMS_${realFormId}`, () => {
            fetchRows();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ realFormId, formId, subscribe ]);

    const onBulkDelete = async (responseIdsToRemove: (string | number)[]) => {
        if (responseIdsToRemove.length === 0 || !realFormId) {
            return;
        }

        setRemoveLoading(true);

        deleteBulkFormResponse(realFormId, responseIdsToRemove.map(id => +id))
        .then((response) => {
            if (response.deletedCount === 0) {
                showMessage('Could not delete the responses. Please contact to admin.', 'warning', 5000);
            }

            showMessage(`${response.deletedCount} response(s) has been deleted.`, 'success', 5000);
            const filteredResults = results.filter(item => !response.deletedResponseIds.includes(+item.response_id));
            setResults(filteredResults);
        })
        .catch(() => {
            showMessage('Couldn\'t remove the record(s). Please try again later.', 'error', 5000);
        })
        .finally(() => {
            setRemoveLoading(false);
            setConfirmModalOpen(false);
        });
    };

    const onDeleteConfirm = async () => {
        if (responseIdsToRemove.current.length === 0 || !realFormId) {
            return;
        }

        setRemoveLoading(true);

        deleteBulkFormResponse(realFormId, responseIdsToRemove.current.map(id => +id))
        .then((response) => {
            if (response.deletedCount === 0) {
                showMessage('Could not delete the responses. Please contact to admin.', 'warning', 5000);
            }

            showMessage(`${response.deletedCount} response(s) has been deleted.`, 'success', 5000);
            const filteredResults = results.filter(item => !response.deletedResponseIds.includes(+item.response_id));
            setResults(filteredResults);
        })
        .catch(() => {
            showMessage('Couldn\'t remove the record(s). Please try again later.', 'error', 5000);
        })
        .finally(() => {
            setRemoveLoading(false);
            setConfirmModalOpen(false);
        });
    };

    const onResponseEdit = (responseId: string | number) => {
        setEditingResponseId(responseId + '');
        setEditModalOpen(true);
    };

    const onAfterUpdate = (updatedResult: any) => {
        setResults(results.map(result => {
            if (+result.response_id === updatedResult.response_id) {
                return {
                    ...result,
                    response: updatedResult.data,
                };
            }

            return result;
        }));
    };

    const onDeleteFormResponse = (responseId: string | number) => {
        setConfirmModalOpen(true);
        responseIdsToRemove.current = [ responseId ];
    };

    const shouldShowColumn = (field: FieldDef): boolean => {
        if (skin === 'skin3' && field.type === 'string') {
            return false;
        }

        return !field.hidden;
    };

    const finalFormName = formName || 'Submitted Results';

    return (
        <div>
            {resultsLoading ? <Loading/> : (
                <div style={{ margin: '16px', width: '98%' }}>
                    <div>
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={skin === 'skin5' ? {
                                color: '#22312d',
                                fontFamily: 'Georgia, \'Times New Roman\', serif',
                                fontWeight: 700,
                            } : undefined}
                        >
                            {finalFormName}
                        </Typography>
                    </div>

                    {formAllFields.length > 0 ? (
                        <FormsResultsTable
                            config={{ fields: formAllFields.filter(shouldShowColumn) }}
                            formName={finalFormName}
                            data={transformFormResponses(results)}
                            onEdit={onResponseEdit}
                            onDelete={onDeleteFormResponse}
                            onDeleteSelected={onBulkDelete}
                            isRemoveLoading={removeLoading}
                            hasActions
                            isUserFriendlyTable
                            isSearchable
                            isSortable
                            skin={skin}
                        />
                    ) : (
                        <Typography variant="h6">
                            Couldn't load the results.
                        </Typography>
                    )}
                </div>
            )}

            <ConfirmModal
                isLoading={removeLoading}
                open={confirmModalOpen}
                title="Are you sure?"
                message="You will not be able to restore the removed data!"
                onConfirm={onDeleteConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />

            <MessageContainer/>

            {editModalOpen && editingResponseId && (
                <Dialog
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    maxWidth={false}
                >
                    <DialogContent
                        sx={{
                            minWidth: 300,
                            width: 'fit-content',
                        }}
                    >
                        <DataUpdateForm
                            formId={realFormId + ''}
                            formType={formType}
                            formAllFields={formAllFields}
                            skin={skin}
                            responseId={editingResponseId}
                            onCancel={() => setEditModalOpen(false)}
                            onAfterUpdate={onAfterUpdate}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default ViewAndEditUserTable;